import { useState } from 'react';
import { useLocation } from 'react-router-dom';

const pageTitles: Record<string, { title: string; subtitle: string }> = {
  '/': { title: 'Dashboard', subtitle: 'Genel bakış ve anlık veriler' },
  '/kampanyalar': { title: 'Kampanya Yönetimi', subtitle: 'Kampanyalarınızı yönetin ve takip edin' },
  '/kampanyalar/ekle': { title: 'Kampanya Ekle', subtitle: 'Yeni bir kampanya oluşturun' },
  '/ziyaretciler': { title: 'Ziyaretçiler', subtitle: 'Ziyaretçi davranışlarını inceleyin' },
  '/event-kayitlari': { title: 'Event Kayıtları', subtitle: 'Tüm etkinlik kayıtlarını görüntüleyin' },
  '/funnel': { title: 'Funnel Analizi', subtitle: 'Dönüşüm hunisi performansı' },
  '/leadler': { title: 'Lead Yönetimi', subtitle: 'Gelen talepleri yönetin ve dönüştürün' },
  '/trafik': { title: 'Trafik Kaynakları', subtitle: 'Trafik kaynaklarını analiz edin' },
  '/utm': { title: 'UTM Analizi', subtitle: 'UTM parametrelerini takip edin' },
  '/raporlar': { title: 'Raporlar', subtitle: 'Detaylı raporlar ve performans analizleri' },
  '/canli': { title: 'Canlı Aktivite', subtitle: 'Anlık ziyaretçi ve event akışı' },
  '/ayarlar': { title: 'Ayarlar', subtitle: 'Sistem yapılandırması ve tercihler' },
};

const salons = ['Tüm Salonlar', 'Beauty Hub İstanbul', 'Glamour Ankara', 'Luxe İzmir', 'Shine Bursa'];

interface HeaderProps {
  collapsed: boolean;
}

export default function Header({ collapsed }: HeaderProps) {
  const location = useLocation();
  const page = pageTitles[location.pathname] || { title: 'Panel', subtitle: '' };
  const [selectedSalon, setSelectedSalon] = useState('Tüm Salonlar');
  const [salonOpen, setSalonOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [dateRange] = useState('10 Mar - 17 Mar 2026');

  return (
    <header
      className={`fixed top-0 right-0 h-[72px] bg-white border-b border-border z-30 flex items-center px-6 transition-all duration-300
        ${collapsed ? 'left-[72px]' : 'left-[260px]'}
      `}
    >
      {/* Page Title */}
      <div className="flex-1 min-w-0">
        <h1 className="text-[20px] font-semibold text-charcoal-600 leading-tight">{page.title}</h1>
        <p className="text-[12px] text-charcoal-300 mt-0">{page.subtitle}</p>
      </div>

      {/* Right Controls */}
      <div className="flex items-center gap-3">
        {/* Search */}
        <div className="relative hidden lg:block">
          <div className="w-4 h-4 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2">
            <i className="ri-search-line text-charcoal-300 text-[15px]" />
          </div>
          <input
            type="text"
            placeholder="Kampanya, ziyaretçi ara..."
            className="w-[240px] h-[38px] pl-9 pr-4 bg-[#FAF8F4] border border-border rounded-full text-[13px] text-charcoal-500 placeholder-charcoal-300 focus:outline-none focus:border-champagne-400 transition-colors"
          />
        </div>

        {/* Date Range */}
        <button className="hidden xl:flex items-center gap-2 h-[38px] px-3.5 bg-[#FAF8F4] border border-border rounded-full text-[13px] text-charcoal-500 hover:border-champagne-400 transition-colors cursor-pointer whitespace-nowrap">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-calendar-line text-champagne-500 text-[14px]" />
          </div>
          {dateRange}
        </button>

        {/* Salon Selector */}
        <div className="relative hidden xl:block">
          <button
            onClick={() => { setSalonOpen(!salonOpen); setNotifOpen(false); setProfileOpen(false); }}
            className="flex items-center gap-2 h-[38px] px-3.5 bg-[#FAF8F4] border border-border rounded-full text-[13px] text-charcoal-500 hover:border-champagne-400 transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-store-2-line text-champagne-500 text-[14px]" />
            </div>
            <span className="max-w-[120px] truncate">{selectedSalon}</span>
            <div className="w-3 h-3 flex items-center justify-center">
              <i className="ri-arrow-down-s-line text-[13px]" />
            </div>
          </button>
          {salonOpen && (
            <div className="absolute top-full mt-2 right-0 w-[200px] bg-white border border-border rounded-2xl shadow-hover py-2 z-50">
              {salons.map(s => (
                <button
                  key={s}
                  onClick={() => { setSelectedSalon(s); setSalonOpen(false); }}
                  className={`w-full text-left px-4 py-2.5 text-[13px] hover:bg-blush-50 transition-colors cursor-pointer whitespace-nowrap
                    ${selectedSalon === s ? 'text-champagne-600 font-medium' : 'text-charcoal-500'}
                  `}
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Notification Bell */}
        <div className="relative">
          <button
            onClick={() => { setNotifOpen(!notifOpen); setSalonOpen(false); setProfileOpen(false); }}
            className="w-[38px] h-[38px] flex items-center justify-center rounded-full hover:bg-blush-50 transition-colors cursor-pointer relative"
          >
            <i className="ri-notification-3-line text-charcoal-400 text-[18px]" />
            <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-400 rounded-full border-2 border-white" />
          </button>
          {notifOpen && (
            <div className="absolute top-full mt-2 right-0 w-[320px] bg-white border border-border rounded-2xl shadow-hover z-50">
              <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                <span className="text-[14px] font-semibold text-charcoal-600">Bildirimler</span>
                <span className="text-[11px] text-champagne-500 font-medium cursor-pointer">Tümünü okundu say</span>
              </div>
              {[
                { icon: 'ri-user-add-line', text: 'Yeni lead: Ayşe Kaya - Botoks Paketi', time: '8 dk önce', color: '#D4AF37' },
                { icon: 'ri-file-list-3-line', text: 'Form gönderimi: Lazer Epilasyon kampanyası', time: '22 dk önce', color: '#7CB342' },
                { icon: 'ri-megaphone-line', text: 'Kampanya bitiyor: Dermapen Paketi', time: '1 sa önce', color: '#FFB74D' },
              ].map((n, i) => (
                <div key={i} className="px-4 py-3 hover:bg-[#FAF8F4] transition-colors cursor-pointer border-b border-border last:border-0">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: `${n.color}15` }}>
                      <i className={`${n.icon} text-sm`} style={{ color: n.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[12px] text-charcoal-500">{n.text}</p>
                      <p className="text-[11px] text-charcoal-300 mt-0.5">{n.time}</p>
                    </div>
                    <div className="w-2 h-2 bg-champagne-400 rounded-full mt-1 flex-shrink-0" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Admin Profile */}
        <div className="relative">
          <button
            onClick={() => { setProfileOpen(!profileOpen); setNotifOpen(false); setSalonOpen(false); }}
            className="flex items-center gap-2 cursor-pointer group"
          >
            <div className="w-[38px] h-[38px] rounded-full overflow-hidden border-2 border-champagne-400 flex items-center justify-center bg-champagne-100 flex-shrink-0">
              <span className="text-champagne-700 text-[14px] font-semibold">AD</span>
            </div>
            {<span className="text-[13px] font-medium text-charcoal-500 hidden xl:block whitespace-nowrap">Admin</span>}
          </button>
          {profileOpen && (
            <div className="absolute top-full mt-2 right-0 w-[200px] bg-white border border-border rounded-2xl shadow-hover py-2 z-50">
              {[
                { icon: 'ri-user-line', label: 'Profil' },
                { icon: 'ri-settings-4-line', label: 'Ayarlar' },
                { icon: 'ri-logout-box-r-line', label: 'Çıkış Yap' },
              ].map(item => (
                <button key={item.label} className="w-full flex items-center gap-3 px-4 py-2.5 text-[13px] text-charcoal-500 hover:bg-blush-50 transition-colors cursor-pointer whitespace-nowrap">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${item.icon} text-charcoal-400`} />
                  </div>
                  {item.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
